import React, { useState } from 'react';
import { X, Music2, ShoppingBag, Trash2 } from 'lucide-react';
import { useBeatsCart } from '../contexts/BeatsCartContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const BeatsCartDrawer = () => {
  const { items, isOpen, total, closeCart, removeItem, clearCart } = useBeatsCart();
  const [loading, setLoading] = useState(false);

  const handleCheckout = async () => {
    if (items.length === 0) return;

    setLoading(true);
    try {
      const cartItems = items.map(item => ({
        beat_id: item.beat.id,
        license_type: item.licenseType
      }));

      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API}/api/beats/checkout`,
        {
          items: cartItems,
          origin_url: window.location.origin
        },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          withCredentials: true
        }
      );

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Checkout failed', error);
      const detail = error?.response?.data?.detail || error?.message || 'Unknown error';
      alert(`Checkout failed: ${detail}`);
    } finally {
      setLoading(false);
    }
  };

  const getLicenseName = (type) => {
    const names = {
      'basic_lease': 'Basic Lease',
      'premium_lease': 'Premium Lease',
      'exclusive_rights': 'Exclusive Rights'
    };
    return names[type] || type;
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/70 z-40 animate-fade-in"
        onClick={closeCart}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full sm:w-[450px] bg-[#0D0D0D] border-l border-[#D4AF37]/20 z-50 flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="p-6 border-b border-[#D4AF37]/20">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <ShoppingBag className="text-[#D4AF37]" size={24} />
              <h2 className="font-['Bebas_Neue'] text-2xl">BEATS CART</h2>
            </div>
            <button
              onClick={closeCart}
              className="p-2 hover:bg-white/5 rounded transition-colors"
              aria-label="Close cart"
            >
              <X size={24} />
            </button>
          </div>
          <p className="text-sm text-[#A3A3A3]">
            {items.length} {items.length === 1 ? 'beat' : 'beats'} in cart
          </p>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {items.length === 0 ? (
            <div className="text-center py-12">
              <Music2 size={48} className="mx-auto text-[#D4AF37]/30 mb-4" />
              <p className="text-[#A3A3A3]">Your beats cart is empty</p>
              <button
                onClick={closeCart}
                className="mt-4 px-6 py-2 bg-[#D4AF37] text-black font-medium hover:bg-[#E5C048] transition-colors"
              >
                Browse Beats
              </button>
            </div>
          ) : (
            items.map((item, index) => (
              <div
                key={`${item.beat.id}-${item.licenseType}-${index}`}
                className="bg-[#1A1A1A] p-4 rounded-lg"
              >
                <div className="flex gap-4">
                  {/* Beat Cover */}
                  <div className="w-16 h-16 bg-[#262626] rounded overflow-hidden flex-shrink-0">
                    {item.beat.cover_art_url ? (
                      <img
                        src={item.beat.cover_art_url}
                        alt={item.beat.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Music2 size={24} className="text-[#D4AF37]/50" />
                      </div>
                    )}
                  </div>

                  {/* Beat Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-white truncate mb-1">
                      {item.beat.title}
                    </h3>
                    <p className="text-xs text-[#D4AF37] uppercase tracking-wider mb-1">
                      {getLicenseName(item.licenseType)}
                    </p>
                    {item.beat.bpm && (
                      <p className="text-xs text-[#A3A3A3]">
                        {item.beat.bpm} BPM {item.beat.key && `• ${item.beat.key}`}
                      </p>
                    )}
                  </div>

                  {/* Price & Remove */}
                  <div className="flex flex-col items-end justify-between">
                    <p className="font-medium text-[#D4AF37]">
                      ${item.price.toFixed(2)}
                    </p>
                    <button
                      onClick={() => removeItem(item.beat.id, item.licenseType)}
                      className="p-1 text-red-400 hover:text-red-300 transition-colors"
                      aria-label="Remove from cart"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer / Checkout */}
        {items.length > 0 && (
          <div className="border-t border-[#D4AF37]/20 p-6 space-y-4">
            {/* Total */}
            <div className="flex items-center justify-between text-lg">
              <span className="font-medium">Total</span>
              <span className="font-['Bebas_Neue'] text-2xl text-[#D4AF37]">
                ${total.toFixed(2)}
              </span>
            </div>

            {/* Checkout Button */}
            <button
              onClick={handleCheckout}
              disabled={loading}
              className="w-full py-4 bg-[#D4AF37] text-black font-medium uppercase tracking-wider hover:bg-[#E5C048] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Processing...' : 'Proceed to Checkout'}
            </button>

            {/* Clear Cart */}
            <button
              onClick={clearCart}
              className="w-full py-2 text-sm text-[#A3A3A3] hover:text-white transition-colors"
            >
              Clear Cart
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default BeatsCartDrawer;
